/**
 * admin-community-api-serverless client.
 *
 * Base path: /api/v1/community/...
 * Auth: Bearer JWT + HMAC headers (x-client-id / x-auth-timestamp / x-client-signature),
 *       all attached by requestCommunityAdmin in ./auth.
 *
 * The acting user is derived server-side from the JWT, so identity body fields
 * (acting_user_id, author_user_id, reporter_user_id, user_id) are NOT sent.
 */

import { requestCommunityAdmin } from './auth';

const PREFIX = '/api/v1/community';

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// Single-resource endpoints return { data }. Extract the payload.
function single(res) {
  return res?.data ?? res;
}

// List endpoints return { data: [...], meta: {...} }. Normalize the envelope.
function list(res) {
  if (res && Array.isArray(res.data)) return { data: res.data, meta: res.meta || {} };
  if (Array.isArray(res)) return { data: res, meta: {} };
  if (res?.data) return { data: res.data, meta: res.meta || {} };
  return { data: [], meta: {} };
}

/* -------------------------------------------------------------------------- */
/* Threads — CRUD                                                              */
/* -------------------------------------------------------------------------- */

export async function getThreadById(id, _opts = {}) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${id}`);
  return single(res);
}

export async function postThread(payload /* , actingUserId */) {
  const body = {
    kind: payload.kind,
    body: payload.body,
  };
  if (payload.title != null) body.title = payload.title;
  if (payload.planet_id != null && payload.planet_id !== '') body.planet_id = Number(payload.planet_id);
  if (payload.latitude != null && payload.latitude !== '') body.latitude = Number(payload.latitude);
  if (payload.longitude != null && payload.longitude !== '') body.longitude = Number(payload.longitude);
  if (payload.gps_verified != null) body.gps_verified = !!payload.gps_verified;
  if (payload.arrival_based != null) body.arrival_based = !!payload.arrival_based;
  if (Array.isArray(payload.tags) && payload.tags.length) body.tags = payload.tags;
  if (payload.board_id != null && payload.board_id !== '') body.board_id = Number(payload.board_id);
  if (payload.board_name) body.board_name = payload.board_name;

  const res = await requestCommunityAdmin(`${PREFIX}/threads`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return single(res);
}

export async function patchThread(id, payload) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return single(res);
}

export async function deleteThread(id) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${id}`, { method: 'DELETE' });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Threads — Feeds & Discovery                                                 */
/* -------------------------------------------------------------------------- */

export async function getThreadsFeed(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads${qs(params)}`));
}

export async function getThreadsNearby(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/nearby${qs(params)}`));
}

export async function getThreadsGlobal(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/global${qs(params)}`));
}

export async function getMyThreads(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/mine${qs(params)}`));
}

export async function getThreadsTrending(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/trending${qs(params)}`));
}

export async function getThreadsGps(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/gps${qs(params)}`));
}

export async function getThreadsByTag(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/by-tag${qs(params)}`));
}

export async function postNearbyUnreadCount(payload) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/nearby/unread-count`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Comments                                                                    */
/* -------------------------------------------------------------------------- */

export async function getComments(threadId, params) {
  return list(await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/comments${qs(params)}`));
}

export async function postComment(threadId, payload /* , actingUserId */) {
  const body = { body: payload.body };
  if (payload.parent_comment_id != null && payload.parent_comment_id !== '') {
    body.parent_comment_id = Number(payload.parent_comment_id);
  }
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return single(res);
}

export async function patchComment(commentId, payload) {
  const res = await requestCommunityAdmin(`${PREFIX}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body: payload.body }),
  });
  return single(res);
}

export async function deleteComment(commentId) {
  const res = await requestCommunityAdmin(`${PREFIX}/comments/${commentId}`, { method: 'DELETE' });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Votes                                                                       */
/* -------------------------------------------------------------------------- */

export async function postVote(threadId, value) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/votes`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  });
  return single(res);
}

export async function deleteVote(threadId) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/votes`, {
    method: 'DELETE',
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Reports                                                                     */
/* -------------------------------------------------------------------------- */

export async function postReport(threadId, payload /* , reporterUserId */) {
  const body = { report_type: Number(payload.report_type) };
  if (payload.detail != null && payload.detail !== '') body.detail = payload.detail;
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/reports`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Views                                                                       */
/* -------------------------------------------------------------------------- */

export async function postThreadView(threadId /* , userId */) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/views`, {
    method: 'POST',
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Boards                                                                      */
/* -------------------------------------------------------------------------- */

export async function postBoard(payload) {
  const body = { name: payload.name };
  if (payload.planet_id != null && payload.planet_id !== '') body.planet_id = Number(payload.planet_id);
  if (payload.latitude != null && payload.latitude !== '') body.latitude = Number(payload.latitude);
  if (payload.longitude != null && payload.longitude !== '') body.longitude = Number(payload.longitude);
  const res = await requestCommunityAdmin(`${PREFIX}/boards`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return single(res);
}

export async function getBoards(params) {
  return list(await requestCommunityAdmin(`${PREFIX}/boards${qs(params)}`));
}

export async function getBoardById(boardId) {
  const res = await requestCommunityAdmin(`${PREFIX}/boards/${boardId}`);
  return single(res);
}

export async function getThreadsByBoard(boardId, params) {
  return list(await requestCommunityAdmin(`${PREFIX}/boards/${boardId}/threads${qs(params)}`));
}

/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */

export async function updateReadDistance(payload) {
  const res = await requestCommunityAdmin(`${PREFIX}/settings/read-distance`, {
    method: 'POST',
    body: JSON.stringify({ read_distance_m: Number(payload.read_distance_m) }),
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Likes — DEPRECATED (superseded by votes)                                    */
/* -------------------------------------------------------------------------- */

export async function postLike(threadId /* , userId */) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/likes`, {
    method: 'POST',
  });
  return single(res);
}

export async function deleteLike(threadId /* , userId */) {
  const res = await requestCommunityAdmin(`${PREFIX}/threads/${threadId}/likes`, {
    method: 'DELETE',
  });
  return single(res);
}

/* -------------------------------------------------------------------------- */
/* Aggregates used by Overview/Map tabs                                        */
/* -------------------------------------------------------------------------- */

// No GET /stats/overview on the real API: aggregate from the global feed.
export async function getOverviewStats() {
  const res = await getThreadsGlobal({ limit: 100 });
  const rows = res.data || [];
  const regional = rows.filter((t) => t.kind === 'regional').length;
  const general = rows.filter((t) => t.kind === 'general').length;
  return {
    total_threads: rows.length,
    regional_count: regional,
    general_count: general,
    // The community API exposes no reports-list/stats endpoint, so this is unknown.
    open_reports: null,
    total_views: rows.reduce((s, t) => s + (t.view_count || 0), 0),
    total_likes: rows.reduce((s, t) => s + (t.like_count || 0), 0),
    total_up: rows.reduce((s, t) => s + (t.up_count || 0), 0),
    total_down: rows.reduce((s, t) => s + (t.down_count || 0), 0),
    comment_count: rows.reduce((s, t) => s + (t.comment_count || 0), 0),
    // Aggregated client-side from the latest 100 threads of the global feed.
    partial: true,
  };
}

// No GET /reports on the real API.
export async function getReports() {
  const e = new Error('Reports list is not available on the community API (no GET /reports endpoint).');
  e.status = 501;
  throw e;
}

export async function getRegionalThreadsForMap() {
  const res = await getThreadsFeed({ kind: 'regional', limit: 100 });
  return (res.data || []).filter((t) => t.latitude != null && t.longitude != null);
}
